import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Menu from './pages/Menu';
import Specials from './pages/Specials';
import BookEvent from './pages/BookEvent';
import Winners from './pages/Winners';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SMSTerms from './pages/SMSTerms';
import EnterDrawing from './pages/EnterDrawing';
import QRLandingPage from './pages/QRLandingPage';
import GeoLandingPage from './pages/GeoLandingPage';
import FindUs from './pages/FindUs';
import Reviews from './pages/Reviews';
import NotFound from './pages/NotFound';
import Redeem from './pages/Redeem';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import DashboardHome from './pages/admin/DashboardHome';
import MenuItems from './pages/admin/MenuItems';
import ForgotPassword from './pages/admin/ForgotPassword';
import ResetPassword from './pages/admin/ResetPassword';
import { Toaster } from './components/ui/toaster';

// NOTE: www -> non-www redirect moved to /app/frontend/public/index.html (inline <head> script)
// so it runs before the React bundle loads. For a true server-level 301, configure a
// Cloudflare Bulk Redirect / Page Rule: www.1staveicp.com/* → https://1staveicp.com/$1 (301).

// ScrollToTop Component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Public Layout (with Navbar and Footer)
const PublicLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/menu" element={<PublicLayout><Menu /></PublicLayout>} />
              <Route path="/specials" element={<PublicLayout><Specials /></PublicLayout>} />
              <Route path="/book-event" element={<PublicLayout><BookEvent /></PublicLayout>} />
              <Route path="/winners" element={<PublicLayout><Winners /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
            <Route path="/sms-terms" element={<PublicLayout><SMSTerms /></PublicLayout>} />
            <Route path="/find-us" element={<PublicLayout><FindUs /></PublicLayout>} />
            <Route path="/reviews" element={<PublicLayout><Reviews /></PublicLayout>} />

            {/* Blog / News */}
            <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
            <Route path="/blog/category/:category" element={<PublicLayout><Blog /></PublicLayout>} />
            <Route path="/blog/tag/:tag" element={<PublicLayout><Blog /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
            
            {/* Geo-targeted SEO Landing Pages */}
            <Route path="/ice-cream-mineral-wells-tx" element={<PublicLayout><GeoLandingPage /></PublicLayout>} />
            <Route path="/ice-cream-weatherford-tx" element={<PublicLayout><GeoLandingPage /></PublicLayout>} />
            <Route path="/ice-cream-azle-tx" element={<PublicLayout><GeoLandingPage /></PublicLayout>} />
            <Route path="/ice-cream-aledo-tx" element={<PublicLayout><GeoLandingPage /></PublicLayout>} />
            <Route path="/ice-cream-willow-park-tx" element={<PublicLayout><GeoLandingPage /></PublicLayout>} />
            <Route path="/ice-cream-springtown-tx" element={<PublicLayout><GeoLandingPage /></PublicLayout>} />
            <Route path="/ice-cream-graford-tx" element={<PublicLayout><GeoLandingPage /></PublicLayout>} />
            
            {/* Legacy WordPress URL Redirects — only true semantic equivalents */}
            <Route path="/pastry" element={<Navigate to="/menu" replace />} />
            <Route path="/pastry/" element={<Navigate to="/menu" replace />} />
            <Route path="/pastries" element={<Navigate to="/menu" replace />} />
            <Route path="/pastries/" element={<Navigate to="/menu" replace />} />
            <Route path="/ice-cream" element={<Navigate to="/menu" replace />} />
            <Route path="/ice-cream/" element={<Navigate to="/menu" replace />} />
            <Route path="/about" element={<Navigate to="/find-us" replace />} />
            <Route path="/about/" element={<Navigate to="/find-us" replace />} />
            <Route path="/about-us" element={<Navigate to="/find-us" replace />} />
            <Route path="/about-us/" element={<Navigate to="/find-us" replace />} />

            {/*
              Dead WordPress / Elementor URLs → render NotFound so the page
              emits noindex (instead of soft-redirecting to home, which Google
              treats as cloaking / soft-404 and prevents deindexing).
            */}
            <Route path="/sample-page" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/sample-page/" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/coming-soon" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/coming-soon/" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/elementor-hf/*" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/elementor/*" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/author/*" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/category/*" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/tag/*" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/feed/*" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/comments/*" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/trackback/*" element={<PublicLayout><NotFound /></PublicLayout>} />
            <Route path="/attachment/*" element={<PublicLayout><NotFound /></PublicLayout>} />

            <Route path="/wp-admin/*" element={<Navigate to="/admin/login" replace />} />
            <Route path="/wp-login.php" element={<Navigate to="/admin/login" replace />} />
            
            {/* Standalone Landing Pages - No navbar/footer */}
            <Route path="/enter" element={<EnterDrawing />} />
            <Route path="/p/:slug" element={<QRLandingPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/redeem" element={<Redeem />} />
            <Route
              path="/admin/dashboard/*"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* 404 Catch-all - must be last */}
            <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
          </Routes>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
